/**
 * src/components/teacher-dashboard-components/theme/components/Container.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../ThemeContext';
import { containerStyles, gridStyles } from '../responsive';

const StyledContainer = styled.div`
  ${containerStyles};
  
  ${props => props.$fluid && `
    max-width: 100% !important;
  `}
  
  ${props => props.$center && `
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: ${props.$minHeight || '100vh'};
  `}
  
  ${props => props.$padding && `
    padding: ${props.$padding};
  `}
`;

const StyledGrid = styled.div`
  ${props => gridStyles(props.$cols)};
  
  ${props => props.$gap && `
    gap: ${props.$gap};
  `}
  
  ${props => props.$alignItems && `
    align-items: ${props.$alignItems};
  `}
  
  ${props => props.$justifyContent && `
    justify-content: ${props.$justifyContent};
  `}
`;

const StyledFlex = styled.div`
  display: flex;
  
  ${props => props.$direction && `flex-direction: ${props.$direction};`}
  ${props => props.$wrap && `flex-wrap: ${props.$wrap};`}
  ${props => props.$align && `align-items: ${props.$align};`}
  ${props => props.$justify && `justify-content: ${props.$justify};`}
  ${props => props.$gap && `gap: ${props.$gap};`}
  
  ${props => props.theme.isMobile && props.$mobileDirection && `
    flex-direction: ${props.$mobileDirection};
  `}
`;

/**
 * Container.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const Container = ({
  children,
  fluid = false,
  center = false,
  minHeight,
  padding,
  className,
  ...props
}) => {
  const theme = useTheme();
  
  return (
    <StyledContainer
      theme={theme}
      $fluid={fluid}
      $center={center}
      $minHeight={minHeight}
      $padding={padding}
      className={className}
      {...props}
    >
      {children}
    </StyledContainer>
  );
};

/**
 * Grid Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const GridComponent = ({
  children,
  cols = { xs: 1, md: 2, lg: 3 },
  gap,
  alignItems,
  justifyContent,
  className,
  ...props
}) => {
  const theme = useTheme();
  
  return (
    <StyledGrid
      theme={theme}
      $cols={cols}
      $gap={gap}
      $alignItems={alignItems}
      $justifyContent={justifyContent}
      className={className}
      {...props}
    >
      {children}
    </StyledGrid>
  );
};

/**
 * Flex Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const FlexComponent = ({
  children,
  direction = 'row',
  wrap = 'nowrap',
  align = 'stretch',
  justify = 'flex-start',
  gap,
  mobileDirection,
  className,
  ...props
}) => {
  const theme = useTheme();
  
  return (
    <StyledFlex
      theme={theme}
      $direction={direction}
      $wrap={wrap}
      $align={align}
      $justify={justify}
      $gap={gap}
      $mobileDirection={mobileDirection}
      className={className}
      {...props}
    >
      {children}
    </StyledFlex>
  );
};

Container.Grid = GridComponent;
Container.Flex = FlexComponent;

export default Container;
